import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  ClassDiagnosticStatusReport,
  DiagnosticStatus,
  filterStudentsByStatus,
  getTabIndex,
  StudentReport,
} from './helper/chartHelper';
import { useEffect, useState } from 'react';
import StudentRow from './StudentRow';
import { Button } from 'primereact/button';
import { X } from 'lucide-react';

type Student = StudentReport;

interface Props {
  visible: boolean;
  onHide: () => void;
  selectedStatus: DiagnosticStatus;
  students: ClassDiagnosticStatusReport;
}

const PAGE_SIZE = 5;

const StudentDiagnosticModal: React.FC<Props> = ({ visible, onHide, selectedStatus, students }) => {
  const [activeIndex, setActiveIndex] = useState(getTabIndex(selectedStatus));

  const completedStudents = filterStudentsByStatus(students, DiagnosticStatus.COMPLETED);
  const inProgressStudents = filterStudentsByStatus(students, DiagnosticStatus.IN_PROGRESS);
  const notStartedStudents = filterStudentsByStatus(students, DiagnosticStatus.NOT_STARTED);

  const studentRowTemplate = (student: Student) => <StudentRow student={student} activeIndex={activeIndex} />;
  const renderTable = (data: Student[]) => (
    <DataTable
      value={data}
      dataKey="student.id"
      paginator
      rows={PAGE_SIZE}
      paginatorTemplate="CurrentPageReport PrevPageLink NextPageLink"
      currentPageReportTemplate="{last} of {totalRecords} students"
      className="nc_diagnostic-datatable"
      emptyMessage="No students"
    >
      <Column body={studentRowTemplate} />
    </DataTable>
  );

  useEffect(() => {
    setActiveIndex(getTabIndex(selectedStatus));
  }, [selectedStatus]);

  return (
    <Dialog
      header={
        <>
          <span className="tw-font-atkinson tw-text-neutral-950 tw-font-bold tw-text-xl">Diagnostic Progress</span>
          <button
            aria-label="Close dialog"
            onClick={onHide}
            className="tw-bg-neutral-50 tw-border-none tw-rounded-full tw-px-2 tw-py-1"
          >
            <X size={16} />
          </button>
        </>
      }
      visible={visible}
      onHide={onHide}
      closable={false}
      pt={{
        headerTitle: {
          className: 'tw-flex tw-justify-between',
        },
      }}
      className="tw-w-[550px] tw-h-[550px]"
      footer={<Button className="tw-ml-auto" aria-label="Close" link label="Close" onClick={onHide} />}
    >
      <TabView
        className="nc_diagnostic-modal-tab-view"
        activeIndex={activeIndex}
        onTabChange={e => setActiveIndex(e.index)}
      >
        <TabPanel
          className={`nc_diagnostic-modal-tab-panel ${activeIndex === 0 ? 'active' : ''}`}
          header={
            <>
              Completed{' '}
              <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">
                {completedStudents.length}
              </span>
            </>
          }
        >
          {renderTable(completedStudents)}
        </TabPanel>

        <TabPanel
          className={`nc_diagnostic-modal-tab-panel ${activeIndex === 1 ? 'active' : ''}`}
          header={
            <>
              In progress{' '}
              <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">
                {inProgressStudents.length}
              </span>
            </>
          }
        >
          <div className="tw-flex tw-justify-end tw-px-4 tw-pt-2 tw-pb-1">
            <span className="tw-text-xs tw-font-atkinson tw-font-semibold tw-uppercase tw-text-neutral-500">
              Q&apos;s Answered <span className="tw-normal-case tw-font-normal">(up to ~34)</span>
            </span>
          </div>
          {renderTable(inProgressStudents)}
        </TabPanel>

        <TabPanel
          className={`nc_diagnostic-modal-tab-panel ${activeIndex === 2 ? 'active' : ''}`}
          header={
            <>
              Not started{' '}
              <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">
                {notStartedStudents.length}
              </span>
            </>
          }
        >
          {renderTable(notStartedStudents)}
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default StudentDiagnosticModal;
